import type { ReportingSearchPerformanceDay, ReportingSeoData } from "./reporting"
import { reportingDates } from "./reporting"
import type { OperationalCheckCounts, ReportingOperations } from "./reportingOperations"
import { deriveSeoPageGroups } from "./reportingPageGroups"

export interface SeoTrendPoint {
  date: string
  clicks: number | null
  impressions: number | null
}
/** Cannot collide with a configured product key (keys start with an alphanumeric). */
export const ALL_REPORT_PRODUCTS = "__all__"

const DAYS_PER_WEEK = 7
const MAX_WEEKLY_POINTS = 8
const CALENDAR_DAY_MS = 86_400_000
const MAX_PAGE_MOVERS = 5

/** Calendar arithmetic on GSC date labels, not elapsed Pacific timestamps. */
function offsetSourceDate(date: string, offset: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + offset * CALENDAR_DAY_MS).toISOString().slice(0, 10)
}

function weeklySeoPoints(points: SeoTrendPoint[], start: string, end: string) {
  const lastSunday = offsetSourceDate(end, -new Date(`${end}T00:00:00Z`).getUTCDay())
  return Array.from({ length: MAX_WEEKLY_POINTS }, (_, index) => {
    const weekEnd = offsetSourceDate(lastSunday, -(MAX_WEEKLY_POINTS - 1 - index) * DAYS_PER_WEEK)
    const weekStart = offsetSourceDate(weekEnd, 1 - DAYS_PER_WEEK)
    const observed = points.filter((point) => point.date >= weekStart && point.date <= weekEnd && point.clicks !== null)
    const complete = observed.length === DAYS_PER_WEEK
    return {
      date: weekStart,
      end: weekEnd,
      observedDays: observed.length,
      complete,
      clicks: complete ? observed.reduce((sum, point) => sum + (point.clicks ?? 0), 0) : null,
      impressions: complete ? observed.reduce((sum, point) => sum + (point.impressions ?? 0), 0) : null,
    }
  }).filter((week) => week.end >= start)
}

function comparePageExports(days: ReportingSearchPerformanceDay[], currentDates: string[], priorDates: string[]) {
  const completeDays = days.filter(
    (day) => day.grain === "page" && day.status === "available" && day.paginationComplete,
  )
  const current = completeDays.filter((day) => currentDates.includes(day.date))
  const prior = completeDays.filter((day) => priorDates.includes(day.date))
  const identities = new Set(
    [...current, ...prior].map((day) => JSON.stringify([day.sourceKey, day.sourceProperty, day.queryVersion])),
  )
  const comparable =
    currentDates.length === DAYS_PER_WEEK &&
    priorDates.length === DAYS_PER_WEEK &&
    current.length === DAYS_PER_WEEK &&
    prior.length === DAYS_PER_WEEK &&
    identities.size === 1
  const totals = new Map<
    string,
    { entity: string; currentClicks: number; priorClicks: number; currentImpressions: number; priorImpressions: number }
  >()
  if (comparable) {
    for (const [period, exports] of [
      ["current", current],
      ["prior", prior],
    ] as const) {
      for (const day of exports) {
        for (const row of day.rows) {
          const total = totals.get(row.entity) ?? {
            entity: row.entity,
            currentClicks: 0,
            priorClicks: 0,
            currentImpressions: 0,
            priorImpressions: 0,
          }
          total[`${period}Clicks`] += row.clicks
          total[`${period}Impressions`] += row.impressions
          totals.set(row.entity, total)
        }
      }
    }
  }
  const changes = [...totals.values()].map((row) => ({
    ...row,
    clickChange: row.currentClicks - row.priorClicks,
    percentChange: row.priorClicks > 0 ? (row.currentClicks - row.priorClicks) / row.priorClicks : null,
  }))
  return {
    comparable,
    currentDays: current.length,
    priorDays: prior.length,
    winners: changes
      .filter((row) => row.clickChange > 0)
      .sort((a, b) => b.clickChange - a.clickChange || a.entity.localeCompare(b.entity))
      .slice(0, MAX_PAGE_MOVERS),
    decliners: changes
      .filter((row) => row.clickChange < 0)
      .sort((a, b) => a.clickChange - b.clickChange || a.entity.localeCompare(b.entity))
      .slice(0, MAX_PAGE_MOVERS),
  }
}

export function deriveSeoReport(data: ReportingSeoData) {
  const dates = reportingDates(data.range.start, data.range.end)
  const currentDates = dates.slice(-7)
  const priorDates = dates.slice(-14, -7)
  return data.products.map((product) => {
    const eligibleDates = dates.filter((date) => date >= product.includedFrom)
    const days = data.days.filter((day) => day.productKey === product.key && eligibleDates.includes(day.date))
    const propertyDays = new Map(days.filter((day) => day.grain === "property").map((day) => [day.date, day]))
    const points: SeoTrendPoint[] = eligibleDates.map((date) => {
      const day = propertyDays.get(date)
      const row = day?.status === "available" ? day.rows[0] : null
      return { date, clicks: row?.clicks ?? null, impressions: row?.impressions ?? null }
    })
    const summarize = (period: string[]) => {
      const measured = points.filter((point) => period.includes(point.date) && point.clicks !== null)
      const clicks = measured.reduce((sum, point) => sum + (point.clicks ?? 0), 0)
      const impressions = measured.reduce((sum, point) => sum + (point.impressions ?? 0), 0)
      return {
        observedDays: measured.length,
        expectedDays: period.length,
        clicks: measured.length ? clicks : null,
        impressions: measured.length ? impressions : null,
        ctr: impressions > 0 ? clicks / impressions : null,
        complete: period.length === 7 && measured.length === 7,
      }
    }
    const current = summarize(currentDates)
    const prior = summarize(priorDates)
    const comparable = current.complete && prior.complete
    const clickChange = comparable ? (current.clicks ?? 0) - (prior.clicks ?? 0) : null
    const percentChange =
      clickChange !== null && prior.clicks !== null && prior.clicks > 0 ? clickChange / prior.clicks : null
    const contributions = new Map<string, { entity: string; clicks: number; impressions: number }>()
    const pageDays = days.filter(
      (day) => day.grain === "page" && currentDates.includes(day.date) && day.status === "available",
    )
    for (const day of pageDays) {
      for (const row of day.rows) {
        const previous = contributions.get(row.entity) ?? { entity: row.entity, clicks: 0, impressions: 0 }
        contributions.set(row.entity, {
          entity: row.entity,
          clicks: previous.clicks + row.clicks,
          impressions: previous.impressions + row.impressions,
        })
      }
    }
    const latestAttempts = days.filter((day) => day.latestAttemptStatus !== "available")
    const sources = data.sources.filter((source) => source.productKey === product.key)
    return {
      product,
      sources,
      points,
      weeklyPoints: weeklySeoPoints(points, data.range.start, data.range.end),
      pageGroups: deriveSeoPageGroups(data, product, currentDates, pageDays),
      pageChanges: comparePageExports(days, currentDates, priorDates),
      current,
      prior,
      clickChange,
      percentChange,
      currentDates,
      priorDates,
      topPages: [...contributions.values()]
        .sort((a, b) => b.clicks - a.clicks || a.entity.localeCompare(b.entity))
        .slice(0, 10),
      pageCoverage: {
        observedDays: pageDays.filter((day) => day.status === "available").length,
        expectedDays: currentDates.length,
      },
      latestAttempts: latestAttempts.map((day: ReportingSearchPerformanceDay) => ({
        date: day.date,
        grain: day.grain,
        status: day.latestAttemptStatus,
      })),
      collectedAt:
        days
          .map((day) => day.collectedAt)
          .sort()
          .at(-1) ?? null,
    }
  })
}

/** No smoothing or interpolation: gaps break the path into independent segments. */
export function seoTrendGeometry(points: SeoTrendPoint[], metric: "clicks" | "impressions") {
  const width = 560
  const height = 170
  const left = 45
  const right = 15
  const top = 15
  const bottom = 30
  const max = Math.max(1, ...points.map((point) => point[metric] ?? 0))
  const coordinates = points.map((point, index) => ({
    date: point.date,
    value: point[metric],
    x: left + (index * (width - left - right)) / Math.max(1, points.length - 1),
    y: point[metric] === null ? null : height - bottom - (point[metric] / max) * (height - top - bottom),
  }))
  const segments: string[] = []
  let current = ""
  for (const point of coordinates) {
    if (point.y === null) {
      if (current) segments.push(current)
      current = ""
      continue
    }
    current += `${current ? " L" : "M"}${point.x.toFixed(2)},${point.y.toFixed(2)}`
  }
  if (current) segments.push(current)
  return { width, height, left, top, baseline: height - bottom, max, coordinates, segments }
}

function sumChecks(counts: OperationalCheckCounts[]): OperationalCheckCounts {
  const expectedKnown = counts.length > 0 && counts.every((value) => value.expected !== null)
  return {
    recorded: counts.reduce((sum, value) => sum + value.recorded, 0),
    passed: counts.reduce((sum, value) => sum + value.passed, 0),
    failed: counts.reduce((sum, value) => sum + value.failed, 0),
    expected: expectedKnown ? counts.reduce((sum, value) => sum + (value.expected ?? 0), 0) : null,
    // Sum per-monitor missing checks; over-sampling one monitor cannot hide another's gap.
    unobserved: expectedKnown ? counts.reduce((sum, value) => sum + (value.unobserved ?? 0), 0) : null,
    latencySumMs: counts.reduce((sum, value) => sum + value.latencySumMs, 0),
    latencySamples: counts.reduce((sum, value) => sum + value.latencySamples, 0),
  }
}

/** UI and portable HTML share grouping and arithmetic; environments never blend. */
export function deriveOperationalProduct(product: ReportingOperations["products"][number]) {
  const groups = new Map<string, typeof product.checks.rows>()
  for (const row of product.checks.rows) {
    const key = `${row.kind}:${row.environment}`
    groups.set(key, [...(groups.get(key) ?? []), row])
  }
  return {
    ...product,
    checkGroups: [...groups].map(([key, rows]) => {
      const current = sumChecks(rows.map((row) => row.current))
      const prior = sumChecks(rows.map((row) => row.prior))
      const dates = [...new Set(rows.flatMap((row) => row.daily.map((day) => day.date)))].sort()
      return {
        key,
        kind: rows[0].kind,
        environment: rows[0].environment,
        monitorCount: rows.length,
        current,
        prior,
        currentPassRate: current.recorded ? current.passed / current.recorded : null,
        priorPassRate: prior.recorded ? prior.passed / prior.recorded : null,
        averageLatencyMs: current.latencySamples ? current.latencySumMs / current.latencySamples : null,
        daily: dates.map((date) => ({
          date,
          ...sumChecks(rows.flatMap((row) => row.daily.filter((day) => day.date === date))),
        })),
      }
    }),
    actions: product.checks.rows.flatMap((row) => {
      const actions: { bindingKey: string; priority: string; action: string }[] = []
      if (!row.enabledAtCollection) return actions
      if (row.staleAtCollection === true)
        actions.push({
          bindingKey: row.bindingKey,
          priority: "P0",
          action: `Investigate stale ${row.kind} scheduling (${row.environment}); last result ${row.lastRecordedAt ?? "never"}.`,
        })
      if (row.lastSuccess === false)
        actions.push({
          bindingKey: row.bindingKey,
          priority: "P0",
          action: `Investigate the latest failed ${row.kind} check (${row.environment}); do not resolve incidents from the report.`,
        })
      if (row.lastSuccess === true && row.alertCount !== null && row.alertCount > 0)
        actions.push({
          bindingKey: row.bindingKey,
          priority: "P1",
          action: `Review alert-state drift: latest check passed but ${row.alertCount} alerts remain recorded (${row.environment}).`,
        })
      return actions
    }),
  }
}
